import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card.tsx';

interface JWPlayerError {
    code: number;
    message: string;
    type?: string;
}

/**
 * Only the events this app subscribes to. JW emits many more, and adding one here is how a new
 * subscription gets a typed payload rather than a cast at the call site.
 */
interface JWPlayerEventMap {
    error: JWPlayerError;
    ready: void;
    complete: void;
    mute: { mute: boolean };
    seek: void;
    fullscreen: void;
}

export interface JWPlayerInstance {
    setup: (config: JWPlayerConfig) => void;
    on: <TEvent extends keyof JWPlayerEventMap>(
        event: TEvent,
        callback: (event: JWPlayerEventMap[TEvent]) => void,
    ) => void;
    remove: () => void;
    play: () => void;
    pause: () => void;
    setFullscreen: (fullscreen: boolean) => void;
    /** Optional because an older embed may not expose it so callers must guard. */
    setMute?: (mute: boolean) => void;
    /** Track index into the player's caption list, where 0 is "off" and 1 is the first track. */
    setCurrentCaptions?: (index: number) => void;
}

interface JWPlayerTrack {
    file: string;
    kind: 'captions' | 'chapters' | 'thumbnails';
    label?: string;
    default?: boolean;
}

interface JWPlayerConfig {
    playlist?: string;
    file?: string;
    width: string;
    height: string;
    autostart: boolean;
    mute: boolean;
    primary: string;
    hlshtml: boolean;
    preload: string;
    image?: string;
    stretching?: string;
    aspectratio?: string;
    forceFullScreenOnLandscape?: boolean;
    tracks?: JWPlayerTrack[];
}

declare global {
    interface Window {
        jwplayer: (container: HTMLElement) => JWPlayerInstance;
    }
}

export interface RoadtripNationVideoCardProps {
    jwAssetId: string;
    videoUrl: string;
    thumbUrl: string;
    onPlayerReady?: (player: JWPlayerInstance) => void;
    className?: string;
    /** When true, playback starts automatically when the player is ready (e.g. in fullscreen overlay). */
    autostart?: boolean;
    /** When true, the player is paused. Used to pause off-screen carousel slides. */
    paused?: boolean;
    /** WebVTT captions track. When present, JW shows its own CC toggle. */
    captionsUrl?: string;
    /** Browsers block unmuted autoplay so any autostarting instance must pass this. */
    muted?: boolean;
}

export const RoadtripNationVideoCard = ({
    jwAssetId,
    videoUrl,
    thumbUrl,
    onPlayerReady,
    className,
    autostart = false,
    paused = false,
    captionsUrl,
    muted = false,
}: RoadtripNationVideoCardProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<JWPlayerInstance | null>(null);
    const fallbackVideoRef = useRef<HTMLVideoElement>(null);
    const [useFallback, setUseFallback] = useState(false);

    /**
     * JW reads these once, when it builds the player. Listing them as setup dependencies instead
     * would rebuild the player whenever one changed and drop the video back to 0:00 so they are
     * latched for setup and the effects below drive the player that already exists.
     */
    const autostartRef = useRef(autostart);
    const mutedRef = useRef(muted);
    const onPlayerReadyRef = useRef(onPlayerReady);

    useEffect(() => {
        autostartRef.current = autostart;
        mutedRef.current = muted;
        onPlayerReadyRef.current = onPlayerReady;
    });

    useEffect(() => {
        try {
            playerRef.current?.setMute?.(muted);
        }
        catch {
            // Not built yet, or already torn down. Setup applies the latched value.
        }
    }, [muted]);

    // A caller raising `autostart` after setup is asking the player it already has to start.
    useEffect(() => {
        if (!autostart) return;
        try {
            playerRef.current?.play();
        }
        catch {
            // Not built yet. Setup autostarts from the latched value instead.
        }
    }, [autostart]);

    useEffect(() => {
        if (!paused) return;
        try {
            playerRef.current?.pause();
        }
        catch {
            // Player may not be initialized yet or already destroyed.
        }
        try {
            fallbackVideoRef.current?.pause();
        }
        catch {
            // Native video element not mounted; ignore.
        }
    }, [paused]);

    useEffect(() => {
        let mounted = true;

        const loadJWPlayer = async () => {
            if (typeof window.jwplayer === 'function') return;

            return new Promise<void>((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdn.jwplayer.com/libraries/cgP3ZPI5.js';
                script.async = true;
                script.onload = () => {
                    if (mounted) resolve();
                };
                script.onerror = () => {
                    if (mounted) reject(new Error('Failed to load JWPlayer'));
                };
                document.head.appendChild(script);
            });
        };

        const initializePlayer = async () => {
            try {
                await loadJWPlayer();
                if (!containerRef.current || !mounted) return;

                playerRef.current = window.jwplayer(containerRef.current);

                playerRef.current.on('error', () => {
                    if (mounted && videoUrl) {
                        setUseFallback(true);
                    }
                });

                playerRef.current.on('ready', () => {
                    if (mounted && playerRef.current) {
                        onPlayerReadyRef.current?.(playerRef.current);
                    }
                });

                playerRef.current.setup({
                    file: videoUrl,
                    width: '100%',
                    height: '100%',
                    autostart: autostartRef.current,
                    mute: mutedRef.current,
                    primary: 'html5',
                    hlshtml: true,
                    preload: 'metadata',
                    stretching: 'fill',
                    aspectratio: '16:9',
                    forceFullScreenOnLandscape: true,
                    image: thumbUrl,
                    ...(captionsUrl
                        ? { tracks: [{ file: captionsUrl, kind: 'captions' as const, label: 'English', default: true }] }
                        : {}),
                });
            }
            catch {
                if (mounted && videoUrl) {
                    setUseFallback(true);
                }
            }
        };

        initializePlayer();

        return () => {
            mounted = false;

            // Cleanup JW Player
            if (playerRef.current) {
                try {
                    playerRef.current.remove();
                }
                catch {
                    // Silently ignore cleanup errors
                }
                playerRef.current = null;
            }
        };
        // Only what genuinely needs a different player. `autostart`, `muted` and `onPlayerReady`
        // are handled above, because rebuilding for them restarts the video.
    }, [jwAssetId, videoUrl, thumbUrl, captionsUrl]);

    const cardClassName = className ?? 'h-60 p-0 m-0 bg-transparent border-secondary-light overflow-hidden';

    if (useFallback && videoUrl) {
        return (
            <Card className={cardClassName}>
                <div className="flex align-top justify-center h-full w-full overflow-hidden">
                    <video
                        key={videoUrl}
                        ref={fallbackVideoRef}
                        controls
                        muted={muted}
                        width="100%"
                        height="100%"
                        className="object-cover"
                    >
                        <source src={videoUrl} type="video/mp4" />
                        {captionsUrl && (
                            <track kind="captions" src={captionsUrl} srcLang="en" label="English" default />
                        )}
                    </video>
                </div>
            </Card>
        );
    }

    return (
        <Card className={cardClassName}>
            <div className="flex align-top justify-center h-full w-full overflow-hidden">
                <div
                    ref={containerRef}
                    className="w-full h-full"
                />
            </div>
        </Card>
    );
};
